import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CalendarOff, Loader2, MessageCircle, ShieldCheck, Sparkles, Star } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LanguageFlag from "@/components/LanguageFlag";
import TutorProfileBody from "@/components/tutor/TutorProfileBody";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { graphqlClient } from "@/lib/graphql";
import { graphql } from "@/__generated__";
import { formatMediumDate } from "@/lib/dates";
import { formatUsdWhole } from "@/lib/money";
import { captureReferralFromUrl } from "@/lib/referral";
import NotFound from "./NotFound";
import { plainBioText } from "@/lib/bioText";

// A tutor's public page, /tutors/<slug> (#669): what a visitor reads without an
// account. The server renders it (TutorPagesController) with exactly this query,
// pinned by spec/requests/tutor_pages_spec.rb, and seeds the answer under
// publicTutorKey. Booking, messaging and saving stay behind sign-up: every button
// here leads to it, and brings the visitor back to this tutor afterwards.
export const PUBLIC_TUTOR_QUERY = graphql(`
  query PublicTutor($slug: String!) {
    tutor(slug: $slug, publicPage: true) {
      id
      slug
      profileTitle
      about
      teachingExperience
      hourlyRateCents
      acceptingBookings
      awayUntil
      videoUrl
      teachingLanguage { id name code }
      spokenLanguages { id language { id name code } level }
      credentials { id kind institution title degreeType field yearObtained status }
      verifiedEducator
      averageRating
      reviewCount
      recentReviews(limit: 10) { id rating comment studentInitials createdAt reply { id body createdAt } }
      user { id fullName initials }
    }
  }
`);

// The photo and the intro video come with links that expire (MediaUrls), so they
// are not in the server render, which is cached: the browser asks for them once
// the page is up, and the initials show meanwhile.
const PUBLIC_TUTOR_MEDIA_QUERY = graphql(`
  query PublicTutorMedia($slug: String!) {
    tutor(slug: $slug, publicPage: true) {
      id
      introVideoUrl
      user { id avatarUrl }
    }
  }
`);

export const publicTutorKey = (slug: string | undefined) => ["publicTutor", slug] as const;

const PublicTutorProfile = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const { user } = useAuth();
  // Media is fetched in the browser only: rendering it on the server would put
  // an expiring link in cached HTML.
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    // An invite link may point here with ?ref=<code> (#1120); it counts like one
    // pointed at /signup.
    captureReferralFromUrl();
  }, []);

  const { data: tutor, isLoading } = useQuery({
    queryKey: publicTutorKey(slug),
    queryFn: async () => (await graphqlClient.request(PUBLIC_TUTOR_QUERY, { slug: slug ?? "" })).tutor,
    enabled: !!slug,
    staleTime: 5 * 60_000,
  });

  const { data: media } = useQuery({
    queryKey: ["publicTutorMedia", slug],
    queryFn: async () => (await graphqlClient.request(PUBLIC_TUTOR_MEDIA_QUERY, { slug: slug ?? "" })).tutor,
    enabled: !!slug && !!tutor && mounted,
    staleTime: 60 * 60_000,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!tutor) return <NotFound />;

  // A signed-in student reads the same tutor inside the app, where booking works.
  if (user?.role === "student") return <Navigate to={`/student/tutor/${tutor.id}`} replace />;

  const signedIn = !!user;
  const name = tutor.user.fullName;
  const language = tutor.teachingLanguage.name;
  const returnTo = encodeURIComponent(`/student/tutor/${tutor.id}`);
  const signUpHref = `/signup?returnTo=${returnTo}`;
  const signInHref = `/signin?returnTo=${returnTo}`;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet>
        <title>{`${name}, ${language} tutor | Jynio`}</title>
      </Helmet>
      <Header />
      {/* py-24 like the other public pages: the site header is fixed, 64px tall. */}
      <main id="main-content" className="flex-1 px-4 py-24 sm:px-6">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row gap-6">
            <Avatar className="w-24 h-24 shrink-0">
              <AvatarImage src={media?.user.avatarUrl || undefined} alt="" />
              <AvatarFallback className="text-2xl">{tutor.user.initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h1 className="text-2xl font-display font-bold text-foreground">{name}</h1>
                <LanguageFlag code={tutor.teachingLanguage.code} className="w-6 h-6" />
                {tutor.verifiedEducator && (
                  <Badge className="gap-1 bg-success/10 text-success hover:bg-success/20">
                    <ShieldCheck className="w-3 h-3" />{t("tutorProfilePage.verifiedEducator")}
                  </Badge>
                )}
                {tutor.awayUntil && (
                  <Badge variant="secondary" className="gap-1">
                    <CalendarOff className="w-3 h-3" />
                    {t("tutorProfilePage.awayUntil", { date: formatMediumDate(tutor.awayUntil, i18n.language) })}
                  </Badge>
                )}
                {tutor.reviewCount > 0 && tutor.averageRating !== null ? (
                  <span className="flex items-center gap-1 text-sm font-medium text-foreground">
                    <Star className="w-4 h-4 fill-primary text-primary" />
                    {tutor.averageRating.toFixed(1)}
                    <span className="text-muted-foreground font-normal">
                      ({t("tutorProfilePage.reviewCount", { count: tutor.reviewCount })})
                    </span>
                  </span>
                ) : (
                  <Badge variant="secondary"><Sparkles className="w-3 h-3 mr-1" />{t("tutorProfilePage.newBadge")}</Badge>
                )}
              </div>
              <p className="text-muted-foreground mb-2">{plainBioText(tutor.profileTitle)}</p>
              <div className="flex items-center gap-4 mb-3">
                <span className="text-lg font-bold text-foreground">
                  {formatUsdWhole(tutor.hourlyRateCents, i18n.language)}
                  <span className="text-xs font-normal text-muted-foreground">{t("tutorProfilePage.perClass")}</span>
                </span>
              </div>

              {!signedIn && (
                <>
                  <div className="flex gap-3 flex-wrap">
                    <Button variant="outline" asChild>
                      <Link to={signUpHref}><MessageCircle className="w-4 h-4 mr-2" />{t("tutorProfilePage.message")}</Link>
                    </Button>
                    {tutor.acceptingBookings ? (
                      <Button asChild><Link to={signUpHref}>{t("tutorProfilePage.bookTrialLesson")}</Link></Button>
                    ) : (
                      <p className="text-sm text-muted-foreground self-center">{t("tutorProfilePage.notAcceptingBookings")}</p>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-3">
                    {t("publicTutorPage.joinBody", { name })}{" "}
                    {t("auth.signUp.haveAccount")}{" "}
                    <Link to={signInHref} className="text-foreground underline focus-ring rounded-sm">{t("nav.signIn")}</Link>
                  </p>
                </>
              )}
            </div>
          </div>

          <TutorProfileBody tutor={tutor} introVideoUrl={media?.introVideoUrl ?? null} />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PublicTutorProfile;
